import { useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import { Broom, DownloadSimple, CircleNotch, CheckCircle, XCircle, Warning } from "@phosphor-icons/react";

export default function Repair() {
  const [input, setInput] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = async () => {
    if (!input.trim()) return;
    setLoading(true);
    setResult(null);
    try {
      const { data } = await api.post("/repair-svg", { svg: input });
      setResult(data);
      toast.success(data.compatible ? "Clean — livery ready" : "Repaired with remaining issues");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const r = new FileReader();
    r.onload = () => setInput(String(r.result));
    r.readAsText(f);
  };

  const download = () => {
    if (!result) return;
    const blob = new Blob([result.svg], { type: "image/svg+xml" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `decal-repaired-${Date.now()}.svg`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="max-w-[1440px] mx-auto">
      <div className="px-6 md:px-10 py-6 border-b border-[var(--border)] flex justify-between items-end flex-wrap gap-4">
        <div>
          <div className="micro-label">/ SVG repair</div>
          <h1 className="font-display font-black text-3xl md:text-4xl tracking-tight mt-1">
            Strip, validate, re-ship.
          </h1>
          <p className="text-[var(--fg-2)] text-sm mt-2 max-w-xl">
            Paste an SVG the game rejected. We'll remove filters, gradients, masks,
            text, scripts and raster embeds.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="btn-ghost cursor-pointer" data-testid="btn-upload-svg">
            Upload .svg
            <input type="file" accept=".svg,image/svg+xml" className="hidden" onChange={onFile} />
          </label>
          <button onClick={run} disabled={!input.trim() || loading} className="btn-primary" data-testid="btn-repair-run">
            {loading ? <><CircleNotch className="animate-spin" size={14} weight="bold"/> Cleaning</> : <><Broom size={14} weight="bold"/> Repair</>}
          </button>
          <button onClick={download} disabled={!result} className="btn-accent" data-testid="btn-download-repaired">
            <DownloadSimple size={14} weight="bold" /> Download
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 divide-x divide-[var(--border)] border-b border-[var(--border)]">
        <div className="bg-white">
          <div className="h-10 flex items-center px-4 border-b border-[var(--border)]">
            <span className="micro-label">Input SVG</span>
          </div>
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder='<svg xmlns="http://www.w3.org/2000/svg">...'
            className="w-full h-[420px] p-4 font-mono text-xs bg-white border-0 resize-none focus:outline-none"
            data-testid="input-svg-textarea"
          />
        </div>
        <div className="bg-white">
          <div className="h-10 flex items-center justify-between px-4 border-b border-[var(--border)]">
            <span className="micro-label">Cleaned output</span>
            {result && (
              <span className={`chip ${result.compatible ? "" : "border-[var(--accent)] text-[var(--accent)]"}`}>
                {result.compatible ? <CheckCircle size={12} weight="fill" className="text-[var(--success)]"/> : <XCircle size={12} weight="fill" />}
                {result.compatible ? "LIVERY READY" : "NOT COMPATIBLE"}
              </span>
            )}
          </div>
          {result ? (
            <div className="grid grid-rows-[auto_1fr]">
              <div className="grid grid-cols-3 divide-x divide-[var(--border)] border-b border-[var(--border)]">
                <Kpi label="Size" value={`${(result.size_bytes / 1024).toFixed(2)} KB`} warn={result.size_bytes > 15360}/>
                <Kpi label="Paths" value={result.path_count} warn={result.path_count > 1000}/>
                <Kpi label="Issues" value={result.issues.length} warn={result.issues.length > 0}/>
              </div>
              <textarea
                value={result.svg}
                readOnly
                className="w-full h-[360px] p-4 font-mono text-xs bg-white border-0 resize-none focus:outline-none"
                data-testid="output-svg-textarea"
              />
            </div>
          ) : (
            <div className="h-[420px] flex items-center justify-center text-[var(--fg-2)] text-sm">
              Output will appear here
            </div>
          )}
        </div>
      </div>

      {result && result.issues.length > 0 && (
        <div className="px-6 md:px-10 py-8" data-testid="issue-list">
          <div className="micro-label mb-3">Diagnostics</div>
          <ul className="divide-y divide-[var(--border)] border border-[var(--border)] bg-white">
            {result.issues.map((i, idx) => (
              <li key={idx} className="p-4 flex items-start gap-3">
                {i.severity === "error" ? (
                  <XCircle size={18} weight="fill" className="text-[var(--accent)] mt-0.5" />
                ) : (
                  <Warning size={18} weight="fill" className="text-[var(--warning)] mt-0.5" />
                )}
                <div>
                  <div className="font-mono text-[11px] tracking-widest">{i.code}</div>
                  <div className="text-sm mt-1">{i.message}</div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function Kpi({ label, value, warn }) {
  return (
    <div className="p-4">
      <div className="micro-label">{label}</div>
      <div className={`font-display font-black text-2xl mt-1 ${warn ? "text-[var(--accent)]" : ""}`}>
        {value}
      </div>
    </div>
  );
}
